import {
  Box,
  BoxProps,
  Menu as ChakraMenu,
  Flex,
  MenuContentProps,
  MenuItemProps,
  MenuRootProps,
  MenuSeparatorProps,
} from '@chakra-ui/react'
import { Fragment, ReactNode, useRef } from 'react'
import { forceDark, forceLight } from 'utils'
import { ButtonProps, CheckBox, IconButton, Text } from '.'
import Button from './Button'
import { CheckIcon, ChevronDownIcon } from './icons'
import { Portal, type PortalProps } from './providers'

export interface Props<V extends string | number, T extends boolean> extends Omit<
  ButtonProps,
  'children' | 'value' | 'onChange'
> {
  /** if provided, show static label in trigger instead of selected value, false to hide */
  buttonLabel?: ReactNode | false
  /** Override default color mode */
  colorMode?: 'light' | 'dark'
  hideCheckbox?: boolean
  isMulti?: T
  label?: ReactNode
  contentProps?: Omit<ListProps, 'children' | 'onChange' | 'checkbox' | 'selectedValues'>
  menuProps?: Omit<MenuRootProps, 'children'> & { matchWidth?: boolean }
  onCancel?: () => void
  onClose?: () => void
  onChange?: (value: T extends true ? V[] : V) => void
  options?: MenuOption<V>[]
  /** (if !label) placeholder text when no option is selected */
  placeholder?: string
  portal?: boolean | Omit<PortalProps, 'children'>
  value?: T extends true ? V[] : V
}

export interface ListProps extends Omit<MenuContentProps, 'children' | 'onChange'> {
  checkbox: 'none' | 'single' | 'multiple'
}

export type MenuOption<V extends string | number> =
  | CustomItem
  | MenuItem<V>
  | MenuDivider
  | MenuGroup<V>

interface CustomItem extends Omit<BoxProps, 'children'> {
  type: 'custom'
  label: ReactNode
}

interface MenuItem<V extends string | number> extends Omit<
  MenuItemProps,
  'children' | 'value' | 'type' | 'onChange' | 'onClick'
> {
  type?: 'item'
  value: V
  label?: string | ReactNode
  icon?: ReactNode
}

interface MenuDivider extends MenuSeparatorProps {
  type: 'divider'
}

interface MenuGroup<V extends string | number> extends Omit<BoxProps, 'children' | 'type'> {
  type: 'group'
  title?: string
  options: MenuItem<V>[]
}

export default function Menu<V extends string | number, T extends boolean = false>({
  buttonLabel,
  colorMode,
  hideCheckbox,
  isMulti,
  label,
  contentProps: listProps,
  menuProps,
  onCancel,
  onClose,
  onChange,
  options,
  placeholder = 'Select...',
  portal,
  value,
  ...rest
}: Props<V, T>) {
  const changedValue = useRef('')

  function handleChange(val: string) {
    changedValue.current = val
    if (!onChange) return

    if (!isMulti) return onChange(val as any)

    let selectedIds = [...((value ?? []) as string[])]
    if (!selectedIds.some(v => v === val)) selectedIds.push(val)
    else selectedIds = selectedIds.filter(v => v !== val)
    onChange(selectedIds as any)
  }

  const selectedItemLabel = isMulti
    ? (value as V[])?.length
      ? `${(value as V[])?.length} Selected`
      : ''
    : getSelectedItemLabel(options, value as V)
  const _buttonLabel =
    buttonLabel === false ? null : buttonLabel || selectedItemLabel || placeholder
  const ColorContainer = colorMode === 'light' || colorMode === 'dark' ? Box : Fragment
  const { matchWidth, ...restMenuProps } = menuProps ?? {}

  return (
    <ChakraMenu.Root
      positioning={{
        placement: 'bottom',
        ...(matchWidth ? { sameWidth: true } : {}),
        ...restMenuProps.positioning,
      }}
      closeOnSelect={!isMulti}
      {...restMenuProps}
      onOpenChange={p => {
        menuProps?.onOpenChange?.(p)
        if (!p.open) return onClose?.()

        if (!changedValue.current) onCancel?.()
        changedValue.current = ''
      }}
    >
      <ChakraMenu.Context>
        {menu => (
          <Flex direction="column" gap={2}>
            {typeof label === 'string' ? (
              <Text textStyle={'bodySmall'} opacity={0.7}>
                {label}
              </Text>
            ) : (
              label
            )}
            <ChakraMenu.Trigger asChild>
              <Button
                justify="space-between"
                variant="outline"
                borderRadius="sm"
                overflow="hidden"
                rightIcon={
                  rest.as === IconButton ? undefined : (
                    <ChevronDownIcon
                      fontSize="sm"
                      transition="all 200ms ease"
                      transform={`rotateX(${menu.open ? '180' : '0'}deg)`}
                    />
                  )
                }
                {...(rest as any)}
              >
                {typeof _buttonLabel === 'string' ? (
                  <Text
                    flex={1}
                    textStyle="bodySmall"
                    textAlign="left"
                    fontWeight="medium"
                    truncate
                  >
                    {_buttonLabel}
                  </Text>
                ) : (
                  _buttonLabel
                )}
              </Button>
            </ChakraMenu.Trigger>
          </Flex>
        )}
      </ChakraMenu.Context>

      <Portal disabled={!portal} {...(typeof portal === 'object' ? portal : {})}>
        <ColorContainer
          {...(colorMode === 'light' ? forceLight : colorMode === 'dark' ? forceDark : undefined)}
        >
          <ChakraMenu.Positioner>
            <Content
              checkbox={hideCheckbox ? 'none' : isMulti ? 'multiple' : 'single'}
              //@ts-ignore
              selectedValues={isMulti ? value : [value]}
              overflow="auto"
              maxHeight="400px"
              //@ts-ignore
              options={options}
              onChange={onChange && handleChange}
              {...listProps}
            />
          </ChakraMenu.Positioner>
        </ColorContainer>
      </Portal>
    </ChakraMenu.Root>
  )
}

interface ContentProps<V extends string | number> extends ListProps {
  selectedValues?: V[]
  options?: MenuOption<V>[]
  onChange?: (value: V) => void
}

function Content<V extends string | number>({
  checkbox,
  selectedValues = [],
  options,
  onChange,
  ...rest
}: ContentProps<V>) {
  if (!options) return null

  return (
    <ChakraMenu.Content minWidth="unset" {...rest}>
      {options.map((option, index) => {
        switch (option.type) {
          case 'divider': {
            return <ChakraMenu.Separator key={index} />
          }
          case 'group': {
            const { title, options, ...rest } = option

            return (
              <ChakraMenu.ItemGroup key={index} {...rest}>
                {title && <ChakraMenu.ItemGroupLabel>{title}</ChakraMenu.ItemGroupLabel>}
                {options.map(option => (
                  <RenderItem
                    {...option}
                    key={option.value}
                    checkbox={checkbox}
                    selected={selectedValues.includes(option.value)}
                    onChange={onChange}
                  />
                ))}
              </ChakraMenu.ItemGroup>
            )
          }
          case 'custom': {
            const { label } = option
            return <Box key={index}>{label}</Box>
          }
          default: {
            return (
              <RenderItem
                {...option}
                key={option.value}
                checkbox={checkbox}
                selected={selectedValues.includes(option.value)}
                onChange={onChange}
              />
            )
          }
        }
      })}
    </ChakraMenu.Content>
  )
}

interface RenderItemProps<V extends string | number> extends MenuItem<V> {
  checkbox: 'single' | 'multiple' | 'none'
  selected: boolean
  onChange?: (value: V) => void
}

function RenderItem<V extends string | number>({
  label,
  value,
  checkbox,
  icon,
  selected = false,
  onChange,
  ...rest
}: RenderItemProps<V>) {
  const content = label ?? String(value)

  return (
    <ChakraMenu.Item
      cursor={onChange ? 'pointer' : undefined}
      py={2}
      pl={3}
      alignItems="center"
      gap={2}
      value={String(value)}
      onClick={onChange ? () => onChange(value) : undefined}
      {...rest}
    >
      {(() => {
        switch (checkbox) {
          case 'single':
            return <CheckIcon size="sm" fill={selected ? undefined : 'transparent'} />
          case 'multiple':
            return <CheckBox size="sm" checked={selected} onChange={() => onChange?.(value)} />
          default:
            return null
        }
      })()}

      {icon}

      {typeof content === 'string' ? (
        <Text textStyle="bodySmall" fontWeight="normal">
          {content}
        </Text>
      ) : (
        content
      )}
    </ChakraMenu.Item>
  )
}

function getSelectedItemLabel<V extends string | number>(options?: MenuOption<V>[], selected?: V) {
  if (!selected || !options) return

  for (const option of options) {
    if ((!option.type || option.type === 'item') && option.value === selected) {
      return option.label ?? option.value
    }

    if (option.type === 'group') {
      const selectedItem = option.options.find(option => option.value === selected)
      if (selectedItem) return selectedItem.label ?? selectedItem.value
    }
  }
}
